'use client'

import { useState, useEffect, useCallback } from 'react'
import Image from 'next/image'
import { cva, type VariantProps } from 'class-variance-authority'
import Award from '@/components/ui/Award'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import Pill from '@/components/ui/Pill'
import { Icon } from '@/icons/Icon'
import { createLogger } from '@/lib/logger'
import { getBrandColors, cn } from '@/lib/utils'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { fetchDealCountsForApps, isMainListApp } from '@/components/content/best/helpers'
import type { Category, App, Media, AppCategoryRecommendation } from '@/payload-types'

const logger = createLogger('BestThumbnail')

const container = cva('flex flex-col', {
  variants: {
    type: {
      thumbnail:
        'w-full relative group overflow-hidden cursor-pointer pl-10 pt-10 rounded-2xl gap-7 border border-white/15 transition-colors hover:border-primary-400/50 hover:shadow-[0_0_25px_5px_rgba(255,52,101,0.25)]',
      card: 'w-full bg-gradient relative overflow-hidden rounded-2xl border border-white/15',
    },
  },
})

type BestThumbnailProps = VariantProps<typeof container> & {
  category: Pick<Category, 'id' | 'name' | 'slug' | 'icon' | 'lastPublishedAt'> & {
    appRecommendations?: Category['appRecommendations']
  }
  apps?: App[]
  type: 'thumbnail' | 'card'
  // Server-provided deal count (e.g. on /best/:slug) — skips the client fetch.
  dealCount?: number
}

const CategoryIcon = ({
  icon,
  size = 12,
  containerClassName,
}: {
  icon: string
  size?: number
  containerClassName: string
}) => (
  <div className={containerClassName}>
    <Icon name={icon} className="text-white" size={size} strokeWidth={1} />
  </div>
)

const MetaInfo = ({ appCount, dealCount }: { appCount: number; dealCount: number }) => (
  <p className="text-13 leading-none text-white/60 [&>span]:font-medium">
    <span>{appCount}</span> app{appCount === 1 ? '' : 's'} and <span>{dealCount}</span> deal
    {dealCount === 1 ? '' : 's'}
  </p>
)

function ScreenshotGallery({ categoryApps }: { categoryApps: App[] }) {
  const [failedIds, setFailedIds] = useState<Set<number>>(new Set())

  const handleError = useCallback((id: number) => {
    setFailedIds((prev) => new Set(prev).add(id))
  }, [])

  const visibleApps = categoryApps?.filter((app) => {
    const homepageImage = app.homepageImage as Media
    return homepageImage?.id && homepageImage?.url && !failedIds.has(homepageImage.id)
  })

  return (
    <div className="relative flex w-full after:absolute after:bottom-0 after:left-0 after:z-20 after:h-1/4 after:w-full after:bg-gradient-to-t after:from-black after:to-transparent">
      {visibleApps?.map((app, index) => {
        const homepageImage = app.homepageImage as Media

        return (
          <div
            key={homepageImage.id}
            className={cn(
              'relative aspect-screen w-1/2 shrink-0 overflow-hidden rounded-lg shadow-[0_40px_50px_-12px_#000,0_0_1px_0_rgba(255,255,255,1)]',
              index > 0 && 'transition-transform duration-1000 ease-in-out',
              index === 1 && 'translate-x-[-50%] group-hover:translate-x-[-40%]',
              index === 2 && 'translate-x-[-100%] group-hover:translate-x-[-80%]',
            )}
            style={{ zIndex: visibleApps.length - index }}
          >
            <Image
              src={homepageImage.url || ''}
              alt={app.name}
              width={homepageImage.width ?? 800}
              height={homepageImage.height ?? 516}
              sizes="(max-width: 768px) 50vw, 25vw"
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => handleError(homepageImage.id)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default function BestThumbnail({
  category,
  apps: appsProp,
  type = 'thumbnail',
  dealCount: dealCountProp,
}: BestThumbnailProps) {
  const [dealCount, setDealCount] = useState(dealCountProp ?? 0)

  // When apps are passed directly (pre-filtered by fetcher), use them.
  // Otherwise fall back to extracting from the category join and filtering client-side.
  const categoryApps: App[] =
    appsProp ??
    (category?.appRecommendations?.docs as AppCategoryRecommendation[])
      ?.filter(isMainListApp)
      ?.map((r) => r.app as App) ??
    []

  // Fetch deal counts client-side
  const appIds = categoryApps.map((app) => app.id).filter(Boolean) as number[]
  const appIdsKey = appIds.join(',')

  useEffect(() => {
    if (dealCountProp !== undefined) return // server-provided on /best/:slug
    if (appIds.length === 0) return

    async function fetchAndSetDealCount() {
      try {
        const { dealCount } = await fetchDealCountsForApps(appIds)
        setDealCount(dealCount)
      } catch (error) {
        logger.error('Failed to fetch deal counts:', error)
      }
    }

    fetchAndSetDealCount()
  }, [appIdsKey, dealCountProp])

  if (!category?.name) return null

  const appCount = categoryApps?.length ?? 0
  const href = `/best/${category.slug}`

  if (type === 'card') {
    return (
      <div className={cn(container({ type }))}>
        <div className="my-14 flex flex-col gap-6 md:my-20">
          <div className="flex justify-center gap-6">
            {categoryApps?.map((app, index) => {
              const appIcon = app.icon as Media
              const appGlyph = app.glyph as Media

              if (!appIcon && !appGlyph) return null
              const { backgroundColor } = getBrandColors(app)

              return (
                <AppIcon
                  key={`${app.name}-${index}`}
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  background={backgroundColor}
                  size={64}
                  invert
                />
              )
            })}
          </div>
        </div>

        <div className="mt-auto flex flex-col gap-5 px-6 pb-6">
          <div className="flex items-center gap-5">
            <CategoryIcon
              icon={category.icon || ''}
              size={12}
              containerClassName="flex h-11 w-11 items-center justify-center rounded-full border border-white/15"
            />
            <Award recommendation={Recommendation.TopPick} size="h-8 w-4" gap="gap-3" />
            <div className="flex flex-col gap-3">
              <span className="font-semibold leading-none text-white">{category.name}</span>
              <MetaInfo appCount={appCount} dealCount={dealCount} />
            </div>
          </div>
          <Link href={href} prefetch={PREFETCH}>
            <Pill style="primary">View List</Pill>
          </Link>
        </div>
      </div>
    )
  }

  const appScreenshotsWithImage = categoryApps?.filter((app) => app.homepageImage !== null)
  const appScreenshots = appScreenshotsWithImage?.slice(0, 3)

  return (
    <Link href={href} prefetch={PREFETCH}>
      <div className={cn(container({ type }))}>
        <div className="flex items-center gap-6 pr-10">
          <Award recommendation={Recommendation.TopPick} size="h-10" gap="gap-1.5">
            <CategoryIcon
              icon={category.icon ?? ''}
              size={12}
              containerClassName="flex h-7 w-7 items-center justify-center rounded-lg border border-white/15"
            />
          </Award>
          <div className="flex flex-col gap-4">
            <span className="font-semibold leading-none text-white">{category.name}</span>
            <MetaInfo appCount={appCount} dealCount={dealCount} />
          </div>
        </div>
        <ScreenshotGallery categoryApps={appScreenshots} />
      </div>
    </Link>
  )
}
